import fs from 'fs';
import path from 'path';
import { isDevelopment } from './env-constants.js';

// Written by scripts/compile-pages.js. The path under this directory is the
// page's url segments joined, so a url lookup maps to a file read.
const LOCAL_PAGES_DIR = path.resolve(process.cwd(), '.local-pages');

// contentful.js v10+ takes [in]/[all] values as arrays; v9 took comma-joined
// strings. Accept either so the fixture lookup can't break on that shape.
function toUrlSegments(value) {
  return Array.isArray(value) ? value : String(value).split(',');
}

function walkFixtures(dir) {
  const files = [];
  for (const item of fs.readdirSync(dir, { withFileTypes: true })) {
    const fullPath = path.join(dir, item.name);
    if (item.isDirectory()) {
      files.push(...walkFixtures(fullPath));
    } else if (item.name.endsWith('.json')) {
      files.push(fullPath);
    }
  }
  return files;
}

/**
 * Serve `.local-pages/` fixtures ahead of the Contentful API so pages can be
 * authored locally (see .cursor/skills/local-page-authoring/). Gated to
 * development and to the existence of the fixture directory, which is
 * gitignored — so this is inert in every deployed environment.
 *
 * Only `template` lookups by url are intercepted; every other query, and every
 * miss or unreadable fixture, falls through to the real API.
 *
 * The `@template` passthrough preserves the client's type. Consumers of
 * contentful-client take their types from its hand-written .d.ts.
 *
 * @template T
 * @param {T} client
 * @returns {T}
 */
export function wrapClientWithLocalFixtures(client) {
  if (!isDevelopment) return client;

  const originalGetEntries = client.getEntries.bind(client);

  client.getEntries = async function (query = {}) {
    if (query.content_type === 'template' && fs.existsSync(LOCAL_PAGES_DIR)) {
      if (query['fields.url[all]']) {
        const urlParts = toUrlSegments(query['fields.url[all]']);
        const fixturePath = path.join(
          LOCAL_PAGES_DIR,
          urlParts.join('/') + '.json',
        );
        try {
          if (fs.existsSync(fixturePath)) {
            const entry = JSON.parse(fs.readFileSync(fixturePath, 'utf8'));
            return { items: [entry], total: 1 };
          }
        } catch {
          // fall through to API
        }
      }

      if (query['fields.url[in]']) {
        // Contentful `[in]` on the url array field matches pages whose url
        // contains any of the given segments. Mirror that against fixture
        // paths (which are the joined url segments).
        const slugs = toUrlSegments(query['fields.url[in]']);
        const entries = [];
        try {
          for (const fixturePath of walkFixtures(LOCAL_PAGES_DIR)) {
            const segments = path
              .relative(LOCAL_PAGES_DIR, fixturePath)
              .replace(/\.json$/, '')
              .split(path.sep);
            const matches = slugs.some((slug) => {
              return segments.includes(slug);
            });
            if (matches) {
              entries.push(JSON.parse(fs.readFileSync(fixturePath, 'utf8')));
            }
          }
        } catch {
          // fall through to API
        }
        if (entries.length) {
          const results = await originalGetEntries(query);
          // A fixture shadows the API's copy of the same entry. Without this
          // the merged list carries it twice: callers that take the first
          // match are unaffected, but callers that iterate (building path
          // lists, say) would see duplicates and an inflated total.
          const fixtureIds = new Set(
            entries.map((entry) => {
              return entry.sys?.id;
            }),
          );
          const apiItems = (results?.items || []).filter((item) => {
            return !fixtureIds.has(item.sys?.id);
          });
          return {
            items: [...entries, ...apiItems],
            total: entries.length + apiItems.length,
          };
        }
      }
    }
    return originalGetEntries(query);
  };

  return client;
}
